<mat-toolbar [color]="'primary'">
  <a mat-icon-button routerLink="../../resources">
    <mat-icon>arrow_back</mat-icon>
  </a>
  @if (!!resource) {
  <h3 class="employee-name-large" mat-dialog-title>{{ resource.name }} (ID: {{ resource.id }})</h3>
  }
  <span class="flex-spacer"></span>
  @if (!!resource) {
  <button mat-flat-button color="primary" (click)="selectLocation()">
    Change Home Location
  </button>
  <button mat-flat-button color="primary" (click)="showExcludedPropertiesDialog()" class="ml-2">
    {{ (childEntities.resourceRule.store.entities | async)?.length || 0 }} Excluded Properties
  </button>
  <button [disabled]="!isFormDirty()" (click)="saveResource()" color="warn" mat-flat-button class="ml-2">Save</button>
  }
</mat-toolbar>

@if (isLoading || !isFullyLoaded && !isError) {
<mat-progress-bar mode="indeterminate"></mat-progress-bar>
}
@if (isError) {
<mat-card>
  <mat-card-header>
    {{ errorMessage | translate }}
  </mat-card-header>
  <mat-card-actions>
    <button (click)="retryLoad()" mat-button>{{ 'scheduler.general.retry' | translate }}</button>
  </mat-card-actions>
</mat-card>
}

@if (!isLoading && !isError && isFullyLoaded) {
<div class="main-container">
  @if (!!resource) {
  <!-- Top Row - 2 Panels -->
  <div class="top-row">
    <!-- Panel 1: User Details (no title) -->
    <mat-card class="user-details-panel">
      <mat-card-content>
        <div class="user-profile">
          <!-- Profile Image Section -->
          <div class="profile-image-container">
            <div class="image-wrapper" (click)="openImageSelector()">
              @if (resource.imageUrl) {
              <img alt="photo or image of the resource" [src]="resource.imageUrl" class="avatar-image clickable"
                mat-card-avatar />
              <!-- Clear image button -->
              <button type="button" class="clear-image-btn" (click)="clearImage($event)" matTooltip="Remove image">
                <mat-icon>clear</mat-icon>
              </button>
              }
              @if (!resource.imageUrl) {
              <div class="initials-circle clickable">
                {{ getInitials(resource.name) }}
              </div>
              }
              <div class="image-overlay">
                <mat-icon>edit</mat-icon>
              </div>
            </div>
          </div>

          <!-- Resource Properties (merged from resource-property component) -->
          <div class="resource-properties">
            @if (!!resource) {
            <form [formGroup]="resourceForm">
              <div class="resource-profile-container">
                <div class="name-team-container">
                  <!-- Name Field -->
                  <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Name</mat-label>
                    <input placeholder="Name" formControlName="ResourceName" matInput>
                  </mat-form-field>

                  <!-- Team Selection Dropdown with images -->
                  <mat-form-field appearance="fill" class="full-width team-selector">
                    <mat-label>Team</mat-label>
                    <mat-select formControlName="Team" (selectionChange)="onTeamSelectionChange($event.value)">
                      @for (team of teams$ | async; track team.id) {
                      <mat-option [value]="team.id">
                        <div class="team-option-with-image">
                          {{ team.name }}
                        </div>
                      </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
              </div>

              <!-- Hidden fields that are still part of the form -->
              <div style="display: none;">
                <input formControlName="ResourceImageURL" type="text">
                <input formControlName="ResourceCost" type="number">
              </div>

              <!-- Work Mode as a checkbox instead of radio buttons -->
              <div class="work-mode-container">
                <mat-checkbox formControlName="IsOfflineWorker" (change)="onOfflineWorkerChange($event)"
                  color="primary">
                  Offline Worker
                </mat-checkbox>
              </div>

              <!-- Operative efficiency (CR69). 100% = no adjustment; below shortens nothing,
                   the operative simply gets longer per job; above 100 means a faster operative
                   who completes work in less than the base SMV. Constrained 50-150%. -->
              <div class="efficiency-container">
                <mat-form-field appearance="fill" class="efficiency-field" subscriptSizing="dynamic">
                  <mat-label>Efficiency %</mat-label>
                  <input matInput type="number" min="50" max="150" step="1"
                    formControlName="EfficiencyPercentage"
                    matTooltip="100 = standard. 50 = needs twice as long. 150 = completes in two thirds the time. Excluded from meetings and two-person jobs.">
                  @if (resourceForm.get('EfficiencyPercentage')?.invalid) {
                  <mat-error>Efficiency must be between 50 and 150.</mat-error>
                  }
                </mat-form-field>

                <mat-form-field appearance="fill" class="efficiency-date-field">
                  <mat-label>Effective From</mat-label>
                  <input matInput [matDatepicker]="effectiveFromPicker"
                    formControlName="EfficiencyEffectiveFrom">
                  <mat-datepicker-toggle matSuffix [for]="effectiveFromPicker"></mat-datepicker-toggle>
                  <mat-datepicker #effectiveFromPicker></mat-datepicker>
                </mat-form-field>

                <mat-form-field appearance="fill" class="efficiency-date-field">
                  <mat-label>Effective To</mat-label>
                  <input matInput [matDatepicker]="effectiveToPicker"
                    formControlName="EfficiencyEffectiveTo">
                  <mat-datepicker-toggle matSuffix [for]="effectiveToPicker"></mat-datepicker-toggle>
                  <mat-datepicker #effectiveToPicker></mat-datepicker>
                </mat-form-field>
              </div>
            </form>
            }
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Panel 2: Resource Routes (moved from bottom) -->
    @if (resource && resourceDetail && resourceDetail.location) {
    <mat-card class="routes-panel-wide">

      <mat-card-content>
        <scheduler-resource-route-map [resource]="resourceDetail" [dateFrom]="currentDate" [dateTo]="currentDate"
          (ticketSelected)="onTicketSelected($event)">
        </scheduler-resource-route-map>
      </mat-card-content>
    </mat-card>
    }
  </div>

  <!-- Bottom Row - 3 Panels -->
  <div class="bottom-row">
    <!-- Skills Component -->
    <div class="skills-panel">
      @if (resource) {
      <app-resource-skill-mapping [resource]="resource"></app-resource-skill-mapping>
      }
    </div>

    <!-- Zones Component -->
    <div class="zones-panel">
      @if (resource) {
      <app-resource-zone-mapping (entityChange)="filterZones()" [resource]="resource"></app-resource-zone-mapping>
      }
    </div>

    <!-- Roster Assignments Component -->
    <div class="roster-assignments-panel">
      @if (resource) {
      <app-resource-roster-assignments [resource]="resource"></app-resource-roster-assignments>
      }
    </div>
  </div>
  }
</div>
}